import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { getStudentProgress } from '../services/activityService';
import './ProgressTracker.css';

const ProgressTracker = () => {
  const { currentUser } = useUser();
  const [progress, setProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    setIsLoading(true);
    getStudentProgress(currentUser.id).then(({ data }) => {
      setProgress(data);
      setIsLoading(false);
    });
  }, [currentUser]);

  if (isLoading || !progress) {
    return (
      <div className="progress-tracker">
        <h2>Your Learning Progress</h2>
        <p style={{ textAlign: 'center', padding: '2rem' }}>Loading progress...</p>
      </div>
    );
  }

  const stats = (type) => {
    const { correct = 0, total = 0 } = progress[type] || {};
    const incorrect = total - correct;
    const correctPct = total === 0 ? 0 : Math.round((correct / total) * 100);
    const incorrectPct = total === 0 ? 0 : Math.round((incorrect / total) * 100);
    return { correct, incorrect, total, correctPct, incorrectPct };
  };

  const overallCorrect = progress.sight.correct + progress.scramble.correct + progress.category.correct + (progress.math_concepts ? progress.math_concepts.correct : 0);
  const overallTotal   = progress.sight.total   + progress.scramble.total   + progress.category.total   + (progress.math_concepts ? progress.math_concepts.total   : 0);
  const overall = overallTotal === 0 ? 0 : Math.round((overallCorrect / overallTotal) * 100);

  // Daily Living "scenarios completed" is a separate stat (DLINT-02) —
  // intentionally excluded from the word-based overall accuracy ring above.
  const scenariosCompleted = progress.scenariosCompleted || 0;

  return (
    <div className="progress-tracker">
      <h2>Your Learning Progress</h2>

      <div className="progress-overview">
        <div className="progress-circle">
          <div className="progress-ring">
            <div
              className="progress-fill"
              style={{ background: `conic-gradient(#4ECDC4 ${overall * 3.6}deg, #e0e0e0 0deg)` }}
            ></div>
          </div>
          <div className="progress-text">
            <div className="overall-percent">{overall}%</div>
            <div className="overall-total">{overallTotal} questions</div>
          </div>
        </div>

        <div className="progress-details">
          <div className="progress-item">
            <h3 id="sight-label">Sight Words</h3>
            <div className="progress-bar" role="progressbar" aria-valuenow={stats('sight').correctPct} aria-valuemin={0} aria-valuemax={100} aria-labelledby="sight-label">
              <div className="progress-fill success" style={{ width: `${stats('sight').correctPct}%` }}></div>
              <div className="progress-fill rework" style={{ width: `${stats('sight').incorrectPct}%` }}></div>
            </div>
            <span className="progress-label">
              <span className="stat-success">✓ {stats('sight').correct} correct ({stats('sight').correctPct}%)</span>
              {stats('sight').incorrect > 0 && <span className="stat-rework"> · ✗ {stats('sight').incorrect} need rework ({stats('sight').incorrectPct}%)</span>}
            </span>
          </div>

          <div className="progress-item">
            <h3 id="scramble-label">Scramble Words</h3>
            <div className="progress-bar" role="progressbar" aria-valuenow={stats('scramble').correctPct} aria-valuemin={0} aria-valuemax={100} aria-labelledby="scramble-label">
              <div className="progress-fill success" style={{ width: `${stats('scramble').correctPct}%` }}></div>
              <div className="progress-fill rework" style={{ width: `${stats('scramble').incorrectPct}%` }}></div>
            </div>
            <span className="progress-label">
              <span className="stat-success">✓ {stats('scramble').correct} correct ({stats('scramble').correctPct}%)</span>
              {stats('scramble').incorrect > 0 && <span className="stat-rework"> · ✗ {stats('scramble').incorrect} need rework ({stats('scramble').incorrectPct}%)</span>}
            </span>
          </div>

          <div className="progress-item">
            <h3 id="category-label">Category Words</h3>
            <div className="progress-bar" role="progressbar" aria-valuenow={stats('category').correctPct} aria-valuemin={0} aria-valuemax={100} aria-labelledby="category-label">
              <div className="progress-fill success" style={{ width: `${stats('category').correctPct}%` }}></div>
              <div className="progress-fill rework" style={{ width: `${stats('category').incorrectPct}%` }}></div>
            </div>
            <span className="progress-label">
              <span className="stat-success">✓ {stats('category').correct} correct ({stats('category').correctPct}%)</span>
              {stats('category').incorrect > 0 && <span className="stat-rework"> · ✗ {stats('category').incorrect} need rework ({stats('category').incorrectPct}%)</span>}
            </span>
          </div>

          <div className="progress-item">
            <h3 id="math-label">Math Concepts</h3>
            <div className="progress-bar" role="progressbar" aria-valuenow={stats('math_concepts').correctPct} aria-valuemin={0} aria-valuemax={100} aria-labelledby="math-label">
              <div className="progress-fill success" style={{ width: `${stats('math_concepts').correctPct}%` }}></div>
              <div className="progress-fill rework" style={{ width: `${stats('math_concepts').incorrectPct}%` }}></div>
            </div>
            <span className="progress-label">
              <span className="stat-success">✓ {stats('math_concepts').correct} correct ({stats('math_concepts').correctPct}%)</span>
              {stats('math_concepts').incorrect > 0 && <span className="stat-rework"> · ✗ {stats('math_concepts').incorrect} need rework ({stats('math_concepts').incorrectPct}%)</span>}
            </span>
          </div>

          <div className="progress-item">
            <h3 id="daily-living-label">Daily Living Skills</h3>
            <div className="progress-bar" role="progressbar" aria-valuenow={scenariosCompleted} aria-valuemin={0} aria-labelledby="daily-living-label">
              <div className="progress-fill success" style={{ width: scenariosCompleted > 0 ? '100%' : '0%' }}></div>
            </div>
            <span className="progress-label">
              <span className="stat-success">
                <i className="fas fa-hands-wash" aria-hidden="true"></i> {scenariosCompleted} scenario{scenariosCompleted !== 1 ? 's' : ''} completed
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressTracker;
